import React, { FC, Fragment, useState } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { Box, Button, Typography } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMutation } from '@apollo/client';
import {
    setOrderReadyForDeliveryTemplate,
    unsetOrderReadyForDeliveryTemplate,
} from 'apollo/templates/transport/mutation.graphql';
import { LoadingButton } from '@mui/lab';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { TransportChannel, TransportOrder, TransportProvider } from 'models/transport';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import InfoText from 'components/shared/info-text.component';
import { transportDateHelper } from 'utils/transport-date-helper';
import ScheduleDeliveryDialog from 'components/shared/schedule-delivery-dialog/schedule-delivery-dialog.component';

type DeliveryActionsProps = {
    order: Order;
    transportProvider?: TransportProvider;
};

const DeliveryActions: FC<DeliveryActionsProps> = ({ order, transportProvider }: DeliveryActionsProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);
    // Read from the order, which is live (DD-3231); both mutations return isReadyForDelivery.
    const isReadyForDelivery = !!order.isReadyForDelivery;
    const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
    const [removeDeliveryConfirmDialogOpen, setRemoveDeliveryConfirmDialogOpen] = useState(false);

    const handleCloseDialog = () => {
        setDeliveryDialogOpen(false);
        setRemoveDeliveryConfirmDialogOpen(false);
    };

    const [setOrderReadyForDelivery, { loading: isSetLoading, error: setError }] = useMutation(
        setOrderReadyForDeliveryTemplate,
        {
            onError: (err) => {
                console.log(err, 'error');
            },
        },
    );

    const [unsetOrderReadyForDelivery, { loading: isUnsetLoading, error: unsetError }] = useMutation(
        unsetOrderReadyForDeliveryTemplate,
        {
            onError: (err) => {
                console.log(err, 'error');
            },
            onCompleted: () => {
                setRemoveDeliveryConfirmDialogOpen(false);
            },
        },
    );

    const handleConfirmDelete = () => {
        unsetOrderReadyForDelivery({
            variables: { orderId: order._id },
        });
    };

    const CurrentDelivery = () => {
        if (!order.currentDelivery) {
            return (
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<LocalShippingIcon />}
                    disableElevation
                    onClick={() => setDeliveryDialogOpen(true)}
                    data-test="delivery-actions.add-transport-btn"
                >
                    {translate('transport:ADD_DELIVERY_TRANSPORT')}
                </Button>
            );
        }
        const currentDelivery = order.currentDelivery as TransportOrder;
        const pickupFromLabDate = new Date(currentDelivery.transportDate);
        const deliveryToClinicDate = currentDelivery.isSameDayDelivery
            ? pickupFromLabDate
            : transportDateHelper.nextWorkingDay(pickupFromLabDate, true);
        return (
            <Box display="flex" alignItems="center">
                <Box mr={1}>
                    <CheckCircleIcon color="success" />
                </Box>
                <Box flexGrow={1}>
                    <Box>
                        <Typography variant="subtitle2">
                            {translate('transport:TRANSPORT_DELIVERY_ALREADY_SCHEDULED')}
                        </Typography>
                    </Box>
                    <Box>{formatDate(deliveryToClinicDate, 'date')}</Box>
                </Box>
            </Box>
        );
    };

    const NShiftDelivery = () => {
        if (isReadyForDelivery) {
            return (
                <Fragment>
                    <Box display="flex" alignItems="center">
                        {!order.isDeliveryCreated ? (
                            <Box flexGrow={1}>
                                <Box>
                                    <Typography variant="subtitle2">
                                        {translate('transport:ADDED_TO_DELIVERY_LIST')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : order.isDeliveryCreated && !order.currentDelivery?.shipmentStatus ? (
                            <Box flexGrow={1}>
                                <Box>
                                    <Typography variant="subtitle2">
                                        {translate('transport:WAYBILL_CREATED')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : undefined}
                        {!order.isDeliveryCreated ? (
                            <Box>
                                <LoadingButton
                                    onClick={() => setRemoveDeliveryConfirmDialogOpen(true)}
                                    loading={isUnsetLoading}
                                    data-test="delivery-actions.remove-from-delivery-btn"
                                >
                                    {translate('common:REMOVE')}
                                </LoadingButton>
                            </Box>
                        ) : undefined}
                    </Box>
                </Fragment>
            );
        }
        return (
            <LoadingButton
                loading={isSetLoading}
                fullWidth
                variant="contained"
                onClick={() =>
                    setOrderReadyForDelivery({
                        variables: { orderId: order._id },
                    })
                }
                startIcon={<LocalShippingIcon />}
                data-test="delivery-actions.add-to-delivery-btn"
            >
                {translate('transport:ADD_TO_DELIVERY_LIST')}
            </LoadingButton>
        );
    };

    return (
        <Fragment>
            {setError ? <AlertSnackbar type="error" message={setError.message} /> : undefined}
            {unsetError ? <AlertSnackbar type="error" message={unsetError.message} /> : undefined}
            {transportProvider?.channel === TransportChannel.DENTHUB_TRANSPORT ? (
                <CurrentDelivery />
            ) : transportProvider?.channel === TransportChannel.NSHIFT ? (
                <NShiftDelivery />
            ) : (
                <InfoText text={translate('orders:NOT_TRANSPORT_ACTION')} />
            )}

            {deliveryDialogOpen ? (
                <ScheduleDeliveryDialog open={deliveryDialogOpen} onClose={handleCloseDialog} order={order} />
            ) : undefined}
            {removeDeliveryConfirmDialogOpen && (
                <ConfirmDialog
                    open={removeDeliveryConfirmDialogOpen || false}
                    handleClose={handleCloseDialog}
                    dialogTitle={translate('transport:REMOVE_DELIVERY')}
                    dialogContent={translate('transport:CONFIRM_REMOVE_DELIVERY')}
                    handleConfirm={handleConfirmDelete}
                />
            )}
        </Fragment>
    );
};

export default DeliveryActions;
